import type { DocCollection } from '@blocksuite/store';
import type { WorkspaceFlavour } from '@pique/env/workspace';

import { Service } from '../../../framework';
import type { BlobStorage, DocStorage } from '../../../sync';
import type { GlobalContextService } from '../../global-context/services/global-context';
import type { WorkspaceMetadata } from '..';
import type { WorkspaceOpenOptions } from '../open-options';
import type {
  WorkspaceEngineProvider,
  WorkspaceFlavourProvider,
} from '../providers/flavour';
import type { WorkspaceDestroyService } from './destroy';
import type { WorkspaceFactoryService } from './factory';
import type { WorkspaceListService } from './list';
import type { WorkspaceProfileService } from './profile';
import type { WorkspaceRepositoryService } from './repo';
import type { WorkspaceTransformService } from './transform';

export class WorkspacesService extends Service {
  get list() {
    return this.listService.list;
  }

  constructor(
    private readonly providers: WorkspaceFlavourProvider[],
    private readonly listService: WorkspaceListService,
    private readonly profileRepo: WorkspaceProfileService,
    private readonly transform: WorkspaceTransformService,
    private readonly workspaceRepo: WorkspaceRepositoryService,
    private readonly workspaceFactory: WorkspaceFactoryService,
    private readonly destroy: WorkspaceDestroyService,
    private readonly globalContextService: GlobalContextService
  ) {
    super();
  }

  get deleteWorkspace() {
    return this.destroy.deleteWorkspace;
  }

  get getProfile() {
    return this.profileRepo.getProfile;
  }

  get transformLocalToCloud() {
    return this.transform.transformLocalToCloud;
  }

  get open() {
    return this.workspaceRepo.open;
  }

  async create(
    flavour: WorkspaceFlavour,
    customize?: (
      docCollection: DocCollection,
      blobStorage: BlobStorage,
      docStorage: DocStorage
    ) => Promise<void>
  ): Promise<WorkspaceMetadata> {
    return this.workspaceFactory.create(flavour, customize);
  }

  openAndActivate(
    options: WorkspaceOpenOptions,
    customProvider?: WorkspaceEngineProvider
  ) {
    const { workspace, dispose } = this.workspaceRepo.open(
      options,
      customProvider
    );
    this.globalContextService.setActiveWorkspaceId(workspace.id);
    return {
      workspace,
      dispose: () => {
        this.globalContextService.setActiveWorkspaceId(null);
        dispose();
      },
    };
  }

  async getWorkspaceBlob(meta: WorkspaceMetadata, blob: string) {
    return await this.providers
      .find(x => x.flavour === meta.flavour)
      ?.getWorkspaceBlob(meta.id, blob);
  }
}
